import axios from 'axios';
import firebase from 'firebase';
import { camelizeKeys, decamelizeKeys as humpsDecamelizeKeys } from 'humps';
import { camelCase } from 'change-case';
import dayjs from 'dayjs';
import { Modal, notification } from 'antd';

/**
 * humps cannot decamelize keys where digits border lowercase letters
 * (e.g. price150ml stays as-is instead of becoming price_150ml).
 * Override those keys explicitly so Sequelize columns match.
 */
const DECAMELIZE_OVERRIDES = { price150ml: 'price_150ml', price500ml: 'price_500ml' };
const decamelizeKeys = (obj) => humpsDecamelizeKeys(obj, {
  process: (key, convert) => DECAMELIZE_OVERRIDES[key] ?? convert(key),
});

const showNotification = (type, title, description) => {
  notification[type]({
    message: title,
    description,
  });
};

const baseURL = process.env.REACT_APP_NODE_ENV === 'development' ? 'http://localhost:3000' : 'https://api.gpmatrix.co.nz';

const client = axios.create({
  baseURL,
  json: true,
  withCredentials: false,
  headers: {
    'Access-Control-Allow-Origin': '*',
    'Access-Control-Allow-Methods': 'GET,PUT,POST,DELETE,PATCH,OPTIONS',
  },
});

// Request interceptor to:
// - attach auth token to request.
// - convert the keys of the outgoing data to snakecase.
client.interceptors.request.use(
  async (config) => {
    const newConfig = { ...config };
    if (firebase.auth().currentUser) {
      newConfig.headers = {
        ...newConfig.headers,
        AuthToken: await firebase.auth().currentUser.getIdToken(true),
      };
    }
    // FormData must not be decamelized (would corrupt multipart body); axios sets boundary when Content-Type is omitted.
    if (newConfig.data instanceof FormData) {
      return newConfig;
    }
    newConfig.data = decamelizeKeys(newConfig.data);
    return newConfig;
  },
  (error) => Promise.reject(error),
);

// Convert the keys of the incoming data to camelcase.
// Skip binary responses: camelizeKeys (humps) treats Blob as a plain object and corrupts the body.
client.interceptors.response.use(
  (response) => {
    const { responseType } = response.config;
    const { data } = response;
    if (
      responseType === 'blob'
      || responseType === 'arraybuffer'
      || data instanceof Blob
      || data instanceof ArrayBuffer
    ) {
      return response;
    }
    return { ...response, data: camelizeKeys(data) };
  },
  (error) => Promise.reject(error),
);

// TODO: Response interceptor to refresh if an auth token has expired.
// https://thedutchlab.com/blog/using-axios-interceptors-for-refreshing-your-api-token

// ========== FRESH CLUB ========

export const getAllOnetimesData = (countryId) => client({
  method: 'POST',
  url: `/recharge/onetimes/${countryId}`,
}).then((response) => response.data)
  .catch((error) => {
    console.log(error);
    showNotification('error', 'Error loading onetimes');
    return Promise.reject(error);
  });

export const getAllCustomerData = (countryId) => client({
  method: 'POST',
  url: `/recharge/customers/${countryId}`,
}).then((response) => response.data)
  .catch((error) => {
    console.log(error);
    showNotification('error', 'Error loading customers');
    return Promise.reject(error);
  });

export const getAllSubscriptionData = (countryId) => client({
  method: 'POST',
  url: `/recharge/subscriptions/${countryId}`,
}).then((response) => response.data)
  .catch((error) => {
    console.log(error);
    showNotification('error', 'Error loading subscriptions');
    return Promise.reject(error);
  });

export const getOnetimeTrend = (countryId) => client({
  method: 'GET',
  url: `/recharge/onetimes/history/${countryId}/trend`,
}).then((response) => response.data)
  .catch((error) => {
    console.log(error);
    showNotification('error', 'Error loading add-to-order trend');
    return Promise.reject(error);
  });

export const getOnetimeHistory = (countryId, month) => client({
  method: 'GET',
  url: `/recharge/onetimes/history/${countryId}`,
  params: { month },
}).then((response) => response.data)
  .catch((error) => {
    console.log(error);
    showNotification('error', 'Error loading add-to-order history');
    return Promise.reject(error);
  });

// new
export const submitOnetimes = (countryId, payload) => client({
  method: 'POST',
  url: `/recharge/onetimes/create/${countryId}`,
  data: payload,
}).then((response) => {
  showNotification('success', 'Onetime submitted!');
  return response.data;
}).catch((error) => {
  console.log(error);
  showNotification('error', 'Error submitting onetimes');
  return Promise.reject(error);
});

// ========== FRESH CLUB DATE ROLL ==========

export const getDateRollSummary = (countryId) => client({
  method: 'GET',
  url: `/recharge/date-roll/summary/${countryId}`,
}).then((response) => response.data)
  .catch((error) => {
    console.log(error);
    showNotification('error', 'Error loading subscription summary');
    return Promise.reject(error);
  });

export const getFreshClubConfig = (countryId) => client({
  method: 'GET',
  url: `/recharge/fresh-club-config/${countryId}`,
}).then((response) => response.data)
  .catch((error) => {
    console.log(error);
    showNotification('error', 'Error loading FRESH Club config');
    return Promise.reject(error);
  });

export const updateFreshClubConfig = (countryId, lookAheadDays) => client({
  method: 'PUT',
  url: `/recharge/fresh-club-config/${countryId}`,
  data: { lookAheadDays },
}).then((response) => response.data)
  .catch((error) => {
    console.log(error);
    showNotification('error', 'Error updating FRESH Club config');
    return Promise.reject(error);
  });

export const startDateRoll = (countryId, targetDate, testMode = false) => client({
  method: 'POST',
  url: `/recharge/date-roll/${countryId}`,
  data: { targetDate, testMode },
}).then((response) => response.data)
  .catch((error) => {
    console.log(error);
    showNotification('error', 'Error starting date roll');
    return Promise.reject(error);
  });

export const getDateRollHistory = () => client({
  method: 'GET',
  url: '/recharge/date-roll/history',
}).then((response) => response.data)
  .catch((error) => {
    console.log(error);
    showNotification('error', 'Error loading roll history');
    return Promise.reject(error);
  });

export const getDateRollStatus = (rollId) => client({
  method: 'GET',
  url: `/recharge/date-roll/${rollId}/status`,
}).then((response) => response.data)
  .catch((error) => {
    console.log(error);
    return Promise.reject(error);
  });

export const getDateRollMissed = (rollId) => client({
  method: 'GET',
  url: `/recharge/date-roll/${rollId}/missed`,
}).then((response) => response.data)
  .catch((error) => {
    console.log(error);
    showNotification('error', 'Error loading missed subscribers');
    return Promise.reject(error);
  });

// ========== WILD CLUB DATE ROLL (shipment order dates) ==========

export const startWildClubDateRoll = (
  countryId, releaseTitle, targetDate, testMode = false,
) => client({
  method: 'POST',
  url: `/recharge/wild-club-date-roll/${countryId}`,
  data: { releaseTitle, targetDate, testMode },
}).then((response) => response.data)
  .catch((error) => {
    console.log(error);
    showNotification('error', 'Error starting Wild Club shipment date roll');
    return Promise.reject(error);
  });

export const getWildClubDateRollHistory = () => client({
  method: 'GET',
  url: '/recharge/wild-club-date-roll/history',
}).then((response) => response.data)
  .catch((error) => {
    console.log(error);
    showNotification('error', 'Error loading Wild Club roll history');
    return Promise.reject(error);
  });

export const getWildClubDateRollStatus = (rollId) => client({
  method: 'GET',
  url: `/recharge/wild-club-date-roll/${rollId}/status`,
}).then((response) => response.data)
  .catch((error) => {
    console.log(error);
    return Promise.reject(error);
  });

export const getWildClubDateRollMissed = (rollId) => client({
  method: 'GET',
  url: `/recharge/wild-club-date-roll/${rollId}/missed`,
}).then((response) => response.data)
  .catch((error) => {
    console.log(error);
    showNotification('error', 'Error loading missed Wild Club orders');
    return Promise.reject(error);
  });

export const getWildClubReleaseTimeline = (
  countryId, { refresh = false, excludeStaff = true } = {},
) => client({
  method: 'GET',
  url: `/recharge/wild-club-date-roll/timeline/${countryId}`,
  params: {
    ...(refresh ? { refresh: 1 } : {}),
    exclude_staff: excludeStaff ? 1 : 0,
  },
}).then((response) => response.data)
  .catch((error) => {
    console.log(error);
    showNotification('error', 'Error loading Wild Club release timeline');
    return Promise.reject(error);
  });

export const getWildClubInsights = (
  countryId, { refresh = false, excludeStaff = true } = {},
) => client({
  method: 'GET',
  url: `/recharge/wild-club/insights/${countryId}`,
  params: {
    ...(refresh ? { refresh: 1 } : {}),
    exclude_staff: excludeStaff ? 1 : 0,
  },
}).then((response) => response.data)
  .catch((error) => {
    console.log(error);
    showNotification('error', 'Error loading Wild Club insights');
    return Promise.reject(error);
  });

// ========== PRODUCTS ==========

export const getProductSkus = (productId) => client({
  method: 'GET',
  url: `/products/${productId}/product-skus`,
}).then(({ data }) => data)
  .catch(() => {
    showNotification('error', 'Error loading Product Skus');
  });

export const getProductSku = (productId, productSkuId) => client({
  method: 'GET',
  url: `/products/${productId}/product-skus/${productSkuId}`,
}).then(({ data }) => data)
  .catch(() => {
    showNotification('error', 'Error loading Product Skus');
  });

export const createProductSku = (productId, data) => client({
  method: 'POST',
  url: `/products/${productId}/product-skus`,
  data,
}).then(({ data: sku }) => sku)
  .catch((error) => {
    showNotification('error', `Error creating Product Sku ${error}`);
    return Promise.reject(error);
  });

export const deleteProductSku = (productId, productSkuId) => client({
  method: 'DELETE',
  url: `products/${productId}/product-skus/${productSkuId}`,
}).catch((error) => {
  showNotification('error', 'Error deleting Product Sku');
});

export const updateProductSku = (productId, productSkuId, data) => client({
  method: 'PUT',
  url: `/products/${productId}/product-skus/${productSkuId}`,
  data,
}).catch(() => {
  showNotification('error', 'Error updating Product Sku');
});

/** Same PUT as updateProductSku but rejects on failure (for inline saves that need to detect errors). */
export const updateProductSkuStrict = (productId, productSkuId, data) => client({
  method: 'PUT',
  url: `/products/${productId}/product-skus/${productSkuId}`,
  data,
});

export const updateProductSkus = async (productId, data) => {
  try {
    const updatePromises = data.map((sku) => {
      // Link fields are written by the sync handler. Sending a stale blank
      // mondayId from the form wipes a write-back that already landed.
      const skuWithoutLinks = { ...sku };
      delete skuWithoutLinks.mondayId;
      delete skuWithoutLinks.productMondayId;
      if (sku.key) {
        console.log('Updating Product Sku');
        return updateProductSku(productId, sku.key, skuWithoutLinks);
      }
      console.log('Creating new Product Sku');
      return createProductSku(productId, skuWithoutLinks);
    });

    return Promise.all(updatePromises);
  } catch (error) {
    showNotification('error', 'Error updating multiple Product Skus');
    return Promise.reject(error);
  }
};

/** Thrown when sync-from-monday returns 409 with candidate Monday items to pick from. */
export class MondaySyncNeedsSelectionError extends Error {
  constructor(candidates, message) {
    super(message || 'Multiple Monday SKUs match. Select one.');
    this.name = 'MondaySyncNeedsSelectionError';
    this.candidates = candidates || [];
  }
}

/**
 * Pull a single SKU's data from Monday.com into Matrix (Monday -> Matrix).
 * @param {string|number} productId
 * @param {string|number} productSkuId
 * @param {string|number} [mondayItemId] - optional explicit Monday item id when auto-match is ambiguous
 * @returns {Promise<object>} updated SKU
 * @throws {MondaySyncNeedsSelectionError} when multiple candidates match
 */
export const syncProductSkuFromMonday = (productId, productSkuId, mondayItemId) => client({
  method: 'POST',
  url: `/products/${productId}/product-skus/${productSkuId}/sync-from-monday`,
  data: mondayItemId != null ? { mondayItemId: String(mondayItemId) } : {},
})
  .then(({ data }) => data)
  .catch((error) => {
    if (error.response?.status === 409 && error.response?.data?.needsSelection) {
      const { candidates, message } = error.response.data;
      return Promise.reject(new MondaySyncNeedsSelectionError(candidates, message));
    }
    showNotification('error', 'Error syncing SKU from Monday');
    return Promise.reject(error);
  });

// ========== SKU Generation ==========

export const previewSkuGeneration = (productId) => client({
  method: 'GET',
  url: `/products/${productId}/generate-skus`,
}).then(({ data }) => data)
  .catch(() => {
    showNotification('error', 'Error previewing SKU generation');
  });

export const generateSkusFromLegacy = (productId, skuIndices) => client({
  method: 'POST',
  url: `/products/${productId}/generate-skus`,
  data: skuIndices ? { skuIndices } : undefined,
}).then(({ data }) => data)
  .catch((error) => {
    showNotification('error', `Error generating SKUs: ${error.message || error}`);
    return Promise.reject(error);
  });

export const getSkuMigrationStatus = () => client({
  method: 'GET',
  url: '/products/sku-migration-status',
}).then(({ data }) => data)
  .catch(() => {
    showNotification('error', 'Error loading SKU migration status');
  });

/** Compare legacy product barcode columns to SKU bn_* (Phase 1 validation). */
export const getSkuMigrationValidation = (params = {}) => client({
  method: 'GET',
  url: '/products/sku-migration-validation',
  params,
}).then(({ data }) => data)
  .catch(() => {
    showNotification('error', 'Error loading SKU migration validation');
  });

/** Apply an auto-fix plan for a single barcode validation issue. */
export const applySkuBarcodeRemediation = (productId, legacyField) => client({
  method: 'POST',
  url: '/products/sku-migration-remediation',
  data: { productId, legacyField },
}).then(({ data }) => data)
  .catch((error) => {
    const message = error?.response?.data?.error || error.message || error;
    showNotification('error', `Error applying barcode fix: ${message}`);
    return Promise.reject(error);
  });

export const previewSapImport = (rows) => client({
  method: 'POST',
  url: '/sap-import/preview',
  data: { rows },
  // Large spreadsheets + DB matching can exceed default browser/proxy expectations; align with long-running imports.
  timeout: 300000,
}).then(({ data }) => data)
  .catch((error) => {
    showNotification('error', `Error previewing SAP import: ${error.message || error}`);
    return Promise.reject(error);
  });

export const commitSapImport = (assignments) => client({
  method: 'POST',
  url: '/sap-import/commit',
  data: { assignments },
}).then(({ data }) => data)
  .catch((error) => {
    showNotification('error', `Error committing SAP import: ${error.message || error}`);
    return Promise.reject(error);
  });

export const getSapImportStatus = () => client({
  method: 'GET',
  url: '/sap-import/status',
}).then(({ data }) => data)
  .catch(() => {
    showNotification('error', 'Error loading SAP import status');
  });

export const getProducts = () => client({
  method: 'GET',
  url: '/products',
}).then(({ data }) => data.map(({ // Convert dates to Day.js objects
  dateReleased, dateAdded, dateLastModified, ...product
}) => ({
  ...product,
  dateReleased: dateReleased ? dayjs(dateReleased, 'DD/MM/YYYY') : dateReleased,
  dateAdded: dateAdded ? dayjs(dateAdded) : dateAdded,
  dateLastModified: dateLastModified ? dayjs(dateLastModified) : dateLastModified,
}))).catch(() => {
  showNotification('error', 'Error loading products');
});

export const getProduct = (productId) => client({
  method: 'GET',
  url: `/products/${productId}`,
}).then((res) => {
  // Convert dates to Day.js objects
  const data = { ...res.data };
  const { dateReleased, dateAdded, dateLastModified } = data;
  data.dateReleased = dateReleased ? dayjs(dateReleased, 'DD/MM/YYYY') : dateReleased;
  data.dateAdded = dateAdded ? dayjs(dateAdded) : dateAdded;
  data.dateLastModified = dateLastModified ? dayjs(dateLastModified) : dateLastModified;
  return data;
}).catch(() => {
  showNotification('error', 'Error loading product');
});

export const createProduct = (data) => client({
  method: 'POST',
  url: '/products',
  data: {
    ...data,
    dateReleased: dayjs.isDayjs(data.dateReleased) ? (
      // Only keep the month and/or year.
      data.dateReleased
        .date(1)
        .minute(0)
        .second(0)
        .millisecond(0)
        .format('DD/MM/YYYY') // TODO: Change to ISO.
    ) : data.dateReleased,
    // Convert dates to ISO format
    dateAdded: data.dateAdded ? dayjs(data.dateAdded).format() : data.dateAdded,
    dateLastModified: dayjs().format(), // Set time last edited as current time
  },
}).then(({ data: productId }) => productId);
// .catch((error) => {
//   showNotification('error', 'Error saving product');
//   // Allow error to be caught where method was called
//   return Promise.reject(error);
// });

export const updateProduct = (productId, data) => client({
  method: 'PUT',
  url: `/products/${productId}`,
  data: {
    ...data,
    dateReleased: dayjs.isDayjs(data.dateReleased) ? (
      // Only keep the month and/or year.
      data.dateReleased
        .date(1)
        .minute(0)
        .second(0)
        .millisecond(0)
        .format('DD/MM/YYYY')// TODO: Change to ISO.
    ) : data.dateReleased,
    // Convert dates to ISO format
    dateAdded: data.dateAdded ? dayjs(data.dateAdded).format() : data.dateAdded,
    dateLastModified: dayjs().format(), // Set time last edited as current time
  },
});
// .catch((error) => {
//   showNotification('error', 'Error saving product');
//   // Allow error to be caught where method was called
//   return Promise.reject(error);
// });

export const deleteProduct = (productId) => client({
  method: 'DELETE',
  url: `/products/${productId}`,
}).then(() => {
  showNotification('success', 'Product deleted!');
}).catch((error) => {
  showNotification('error', 'Error deleting product');
  // Allow error to be caught where method was called
  return Promise.reject(error);
});

// ========== FOODSTUFFS ==========

export const FOODSTUFFS_REGIONS = {
  NORTH: 'north-island',
  SOUTH: 'south-island',
};

export const getFoodstuffsData = (region) => client({
  method: 'GET',
  url: `/foodstuffs/${region}`,
}).then(({ data }) => data)
  .catch(() => {
    showNotification('error', 'Error loading Foodstuffs data');
  });

export const createFoodstuffsItem = (region, data) => client({
  method: 'POST',
  url: `/foodstuffs/${region}`,
  data,
}).then(({ data: itemId }) => {
  showNotification('success', 'Item created!');
  return itemId;
}).catch(() => {
  showNotification('error', 'Error saving item');
});

export const updateFoodstuffsItem = (region, itemId, data) => client({
  method: 'PUT',
  url: `/foodstuffs/${region}/${itemId}`,
  data,
}).then(() => {
  showNotification('success', 'Item updated!');
}).catch(() => {
  showNotification('error', 'Error saving item');
});

export const deleteFoodstuffsItem = (region, itemId) => client({
  method: 'DELETE',
  url: `/foodstuffs/${region}/${itemId}`,
}).then(() => {
  showNotification('success', 'Item deleted!');
}).catch((error) => {
  showNotification('error', 'Error deleting item');
  // Allow error to be caught where method was called
  return Promise.reject(error);
});

// ========== QR CODES ==========

export const getQRCodes = () => client({
  method: 'GET',
  url: '/qr-codes',
}).then(({ data }) => data.map(({
  dateLastModified, ...qrCodeData
}) => ({
  ...qrCodeData,
  // Convert dates to Day.js objects
  dateLastModified: dateLastModified ? dayjs(dateLastModified) : dateLastModified,
})))
  .catch(() => {
    showNotification('error', 'Error loading QR codes');
  });

export const createQRCode = (data) => client({
  method: 'POST',
  url: '/qr-codes',
  data,
}).then(({ data: qrCodeData }) => {
  showNotification('success', 'QR code created!');
  const { qrCodeImageUrl, dateLastModified } = qrCodeData;
  return {
    qrCodeImageUrl,
    dateLastModified: dayjs(dateLastModified),
  };
}).catch((error) => {
  showNotification('error', 'Error saving QR code');
  // Allow error to be caught where method was called
  return Promise.reject(error);
});

export const updateQRCode = (id, data) => client({
  method: 'PUT',
  url: `/qr-codes/${id}`,
  data,
}).then(({ data: qrCodeData }) => {
  showNotification('success', 'QR code updated!');
  const { dateLastModified } = qrCodeData;
  return {
    dateLastModified: dayjs(dateLastModified),
  };
}).catch((error) => {
  showNotification('error', 'Error saving QR code');
  // Allow error to be caught where method was called
  return Promise.reject(error);
});

export const deleteQRCode = (id) => client({
  method: 'DELETE',
  url: `/qr-codes/${id}`,
}).then(() => {
  showNotification('success', 'QR code deleted!');
}).catch((error) => {
  showNotification('error', 'Error deleting QR code');
  // Allow error to be caught where method was called
  return Promise.reject(error);
});

// ========== USERS ==========

export const getUsers = () => client({
  method: 'GET',
  url: '/users',
}).then((res) => {
  const data = res.data.map(({ lastSignInTime, ...user }) => ({
    ...user,
    lastSignInTime: lastSignInTime ? dayjs(lastSignInTime) : lastSignInTime,
  }));
  return data;
}).catch(() => {
  showNotification('error', 'Error loading users');
});

export const createUser = (data) => client({
  method: 'POST',
  url: '/users',
  data,
}).then(({ data: userId }) => {
  showNotification('success', 'User created!', 'An email with their login details has been sent to them.');
  return userId;
}).catch((error) => {
  showNotification('error', 'Error creating user');
  // Allow error to be caught where method was called
  return Promise.reject(error);
});

export const updateUser = (userId, data) => client({
  method: 'PUT',
  url: `/users/${userId}`,
  data,
}).then(() => {
  showNotification('success', 'User updated!');
}).catch((error) => {
  showNotification('error', 'Error updating user');
  // Allow error to be caught where method was called
  return Promise.reject(error);
});

export const deleteUser = (userId) => client({
  method: 'DELETE',
  url: `/users/${userId}`,
}).then(() => {
  showNotification('success', 'User deleted!');
}).catch((error) => {
  showNotification('error', 'Error deleting user');
  // Allow error to be caught where method was called
  return Promise.reject(error);
});

export const setAdminStatus = (userId, admin) => client({
  method: 'POST',
  url: `/users/${userId}/admin-status`,
  data: {
    admin,
  },
}).then(() => {
  showNotification('success', `User is ${admin ? 'now an admin!' : 'no longer an admin'}`);
}).catch(() => {
  showNotification('error', 'Error updating admin status');
});

export const getMiscellaneousData = () => client({
  method: 'GET',
  url: '/misc',
}).then(({ data }) => (
  { // Convert birthday dates to Day.js objects
    ...data,
    birthdays: data.birthdays.map(
      ({ date, ...birthday }) => (
        {
          ...birthday,
          date: dayjs(date),
        }
      ),
    ),
  }
))
  .catch(() => {
  // TODO: Handle error
  });

// ========== DEAR ==========

export const STOCK_LOCATIONS = {
  DISTRIBUTION: 'distribution',
  SCOTT_BASE: 'scott-base',
  CELLARING_STOCK: 'cellaring-stock',
  MOTUS: 'motus',
  PETER_SADLER: 'peter-sadler',
  GROWLER_DEPOT: 'growler-depot',
};

// TODO: Use query params for location instead
export const getStock = (location) => client({
  method: 'GET',
  url: `/dear/stock/${location}`,
}).then((res) => {
  // Convert dates to Day.js objects
  // TODO: convert expiry in each instance
  const data = res.data.map(({ instances, ...product }) => ({
    ...product,
    instances: instances.map(({ expiryDate, ...instance }) => (
      {
        ...instance,
        expiryDate: expiryDate ? dayjs(expiryDate) : expiryDate,
      }
    )),
  }));
  return data;
}).catch(() => {
  showNotification('error', 'Error loading stock');
});

export const getDearProducts = (skuSearch = null, cancelToken = null) => client({
  method: 'GET',
  url: '/dear/products',
  params: {
    skuSearch,
  },
  cancelToken,
}).then((res) => {
  const { data } = res;
  return data;
  // Allow error to be caught where method was called
}).catch((error) => Promise.reject(error));

export const getDearProduct = (sku) => client({
  method: 'GET',
  url: `/dear/products/${sku}`,
}).then((res) => {
  const { data } = res;
  return data;
  // Allow error to be caught where method was called
}).catch((error) => Promise.reject(error));

/** Start Cin7 cache sync (runs in background). Returns { data, status }; 202 = started, 409 = already running. Poll getCin7SyncStatus() for progress. Pass full=true to bypass ModifiedSince and force a full resync. */
export const syncCin7Products = (full = false) => client({
  method: 'POST',
  url: '/dear/sync-all-products',
  params: full ? { full: 'true' } : {},
  validateStatus: (status) => status === 202 || status === 409 || (status >= 200 && status < 300),
}).then((res) => ({ data: res.data, status: res.status })).catch((err) => {
  if (err.response) return { data: err.response.data, status: err.response.status };
  throw err;
});

/** Get current Cin7 sync progress (status, page, totalPages, syncedCount, error). */
export const getCin7SyncStatus = () => client({
  method: 'GET',
  url: '/dear/sync-status',
}).then(({ data }) => data);

/** Fetch all cached Cin7 products. Optional params: status, category, includeAll (true = include Litre etc.). */
export const getCachedCin7Products = (params = {}) => client({
  method: 'GET',
  url: '/dear/cached-products',
  params,
}).then(({ data }) => data);

/** Fetch distinct UOM values in the cache with counts (for inspecting sellable vs ingredient types). */
export const getCachedCin7ProductsUomSummary = () => client({
  method: 'GET',
  url: '/dear/cached-products/uom-summary',
}).then(({ data }) => data);

/** Start Kounta REST API product cache sync (parallel to CSV import). 202 = started, 409 = already running. */
export const syncKountaApiProducts = () => client({
  method: 'POST',
  url: '/kounta-api/sync-products',
  validateStatus: (status) => status === 202 || status === 409 || (status >= 200 && status < 300),
}).then((res) => ({ data: res.data, status: res.status })).catch((err) => {
  if (err.response) return { data: err.response.data, status: err.response.status };
  throw err;
});

/** Poll Kounta API sync progress */
export const getKountaApiSyncStatus = () => client({
  method: 'GET',
  url: '/kounta-api/sync-status',
}).then(({ data }) => data);

/** Paginated cache rows from Kounta API sync (omit raw_payload unless includeRaw). */
export const getKountaApiCachedProducts = (params = {}) => client({
  method: 'GET',
  url: '/kounta-api/cached-products',
  params,
}).then(({ data }) => data);

/** Full raw_payload for one cached product (debug / mapping). */
export const getKountaApiCachedProductRaw = (kountaProductId) => client({
  method: 'GET',
  url: `/kounta-api/cached-products/${encodeURIComponent(kountaProductId)}/raw`,
}).then(({ data }) => data);

/** Durable cache row counts + newest timestamps + in-memory Cin7/Kounta job flags. */
export const getProductCacheOverview = () => client({
  method: 'GET',
  url: '/product-cache/overview',
}).then(({ data }) => data);

/**
 * Refresh one Shopify store into shopify_price_cache (product-cache permission).
 * Crawl can take several minutes.
 */
export const refreshProductCacheShopify = (store = 'GPNZ') => client({
  method: 'POST',
  url: '/product-cache/refresh-shopify',
  data: { store },
  timeout: 300000,
}).then(({ data }) => data);

// ========== PERMISSIONS (admin-managed roles) ==========

export const getMyPermissions = () => client({
  method: 'GET',
  url: '/me/permissions',
}).then(({ data }) => (Array.isArray(data) ? data : []))
  .catch(() => []);

export const getPermissions = () => client({
  method: 'GET',
  url: '/permissions',
}).then(({ data }) => data);

export const getRoles = () => client({
  method: 'GET',
  url: '/roles',
}).then(({ data }) => data);

export const postRole = (body) => client({
  method: 'POST',
  url: '/roles',
  data: body,
}).then(({ data }) => data);

export const putRole = (roleId, body) => client({
  method: 'PUT',
  url: `/roles/${roleId}`,
  data: body,
}).then(({ data }) => data);

export const deleteRole = (roleId) => client({
  method: 'DELETE',
  url: `/roles/${roleId}`,
}).then(({ data }) => data);

export const getUserRoles = (uid) => client({
  method: 'GET',
  url: '/user-roles',
  params: uid ? { uid } : {},
}).then(({ data }) => data);

export const putUserRoles = (body) => client({
  method: 'PUT',
  url: '/user-roles',
  data: body,
}).then(({ data }) => data);

// ========== DTC PRICING MANAGEMENT ========

export const getDtcPricing = () => client({
  method: 'GET',
  url: '/dtc-prices',
}).then(({ data: prices }) => prices)
  .catch(() => {
    showNotification('error', 'Error loading DTC Pricing');
  });

// ========== CERTIFIED FRESH MANAGEMENT ========

export const getCertifiedFresh = () => client({
  method: 'GET',
  url: '/certified-fresh/retailers',
}).then(({ data: retailers }) => retailers)
  .catch(() => {
    showNotification('error', 'Error loading Certified Fresh retailers');
  });

export const updateCertifiedFresh = (retailerId, updateData) => client({
  method: 'PUT',
  url: `/certified-fresh/retailers/${retailerId}`,
  data: updateData,
}).then(({ data }) => {
  showNotification('success', 'Retailer updated!');
  return data;
})
  .catch((error) => {
    showNotification('error', 'Error updating Certified Fresh retailer');
    return Promise.reject(error);
  });

export const createRetailer = (data) => client({
  method: 'POST',
  url: '/certified-fresh/retailers',
  data,
}).then(({ data: retailerId }) => {
  showNotification('success', 'Retailer added!');
  return retailerId;
}).catch((error) => {
  showNotification('error', 'Error adding retailer');
  return Promise.reject(error);
});

export const deleteRetailer = (retailerId) => client({
  method: 'DELETE',
  url: `/certified-fresh/retailers/${retailerId}`,
}).then(() => {
  showNotification('success', 'Retailer deleted!');
}).catch((error) => {
  showNotification('error', 'Error deleting retailer');
  // Allow error to be caught where method was called
  return Promise.reject(error);
});

export const getCoordsFromAddress = (address) => client({
  method: 'POST',
  url: '/certified-fresh/add-coords',
  data: { address },
}).then((res) => res.data)
  .catch((error) => {
    console.error('Error retrieving coordinates:', error.response?.data || error.message);
    showNotification('error', 'Error retrieving coordinates');
    return null;
  });

// ========== DASHBOARDS (NEW REPORTS) ==========

export const getDashboards = () => client({
  method: 'GET',
  url: '/reports',
}).then(({ data: dashboards }) => dashboards)
  .catch(() => {
    showNotification('error', 'Error loading dashboards');
  });

export const createDashboard = (data) => client({
  method: 'POST',
  url: '/reports',
  data,
}).then(({ data: dashboardsId }) => {
  showNotification('success', 'Dashboard added!');
  return dashboardsId;
}).catch((error) => {
  showNotification('error', 'Error adding dashboard');
  // Allow error to be caught where method was called
  return Promise.reject(error);
});

export const updateDashboard = (dashboardsId, data) => client({
  method: 'PUT',
  url: `/reports/${dashboardsId}`,
  data,
}).then(() => {
  showNotification('success', 'Dashboard updated!');
  console.log('dashboard updated');
}).catch((error) => {
  showNotification('error', 'Error updating dashboard');
  // Allow error to be caught where method was called
  return Promise.reject(error);
});

export const deleteDashboard = (dashboardsId) => client({
  method: 'DELETE',
  url: `/reports/${dashboardsId}`,
}).then(() => {
  showNotification('success', 'Dashboard deleted!');
}).catch((error) => {
  showNotification('error', 'Error deleting dashboard');
  // Allow error to be caught where method was called
  return Promise.reject(error);
});

// ========== LEGACY REPORTS ==========

// export const getReports = () => client({
//   method: 'GET',
//   url: '/reports',
// }).then(({ data: categories }) => (
//   Object.entries(categories).reduce((output, [category, sets]) => ({
//     ...output,
//     [category]: sets.map(({ date, ...reports }) => ({
//       ...reports,
//       date: dayjs(date), // Convert dates to Day.js objects
//     })),
//   }), {})))
//   .catch(() => {
//     showNotification('error', 'Error loading reports');
//   });

// export const createReportSet = ({ date, ...data }) => client({
//   method: 'POST',
//   url: '/reports',
//   data: {
//     ...data,
//     date: date.format(),
//   },
// }).then(({ data: reportSetId }) => {
//   showNotification('success', 'Report created!');
//   return reportSetId;
// }).catch((error) => {
//   showNotification('error', 'Error creating report');
//   // Allow error to be caught where method was called
//   return Promise.reject(error);
// });

// export const updateReportSet = (reportSetId, { date, ...data }) => client({
//   method: 'PUT',
//   url: `/reports/${reportSetId}`,
//   data: {
//     ...data,
//     date: date.format(),
//   },
// }).then(() => {
//   showNotification('success', 'Report updated!');
// }).catch((error) => {
//   showNotification('error', 'Error updating report');
//   // Allow error to be caught where method was called
//   return Promise.reject(error);
// });

// export const deleteReportSet = (reportSetId) => client({
//   method: 'DELETE',
//   url: `/reports/${reportSetId}`,
// }).then(() => {
//   showNotification('success', 'Report deleted!');
// }).catch((error) => {
//   showNotification('error', 'Error deleting report');
//   // Allow error to be caught where method was called
//   return Promise.reject(error);
// });

// ========== ACTIVITY ==========

export const getRecentActivity = () => client({
  method: 'GET',
  url: '/activity/recent',
}).then(({ data }) => {
  // Convert dates to Day.js objects and changed fields to camelcase
  const recentActivity = data.map(({ timestamp, changes, ...event }) => ({
    ...event,
    timestamp: dayjs(timestamp),
    changes: (changes || []).map((change) => ({
      ...change,
      field: camelCase(change.field),
    })),
  }));
  return recentActivity;
});

const mapActivityEvents = (events) => (events || []).map(({ timestamp, changes, ...event }) => ({
  ...event,
  timestamp: dayjs(timestamp),
  changes: (changes || []).map((change) => ({
    ...change,
    field: camelCase(change.field),
  })),
}));

export const getProductActivity = (productId, params = {}) => client({
  method: 'GET',
  url: `/products/${productId}/activity`,
  params,
}).then(({ data }) => ({
  events: mapActivityEvents(data.events || data),
  nextBefore: data.nextBefore ?? null,
}));

export const getProductSkuActivity = (productId, skuId, params = {}) => client({
  method: 'GET',
  url: `/products/${productId}/product-skus/${skuId}/activity`,
  params,
}).then(({ data }) => ({
  events: mapActivityEvents(data.events || data),
  nextBefore: data.nextBefore ?? null,
}));

const usageRange = (days) => ({
  from: dayjs().startOf('day').subtract(Math.max(days, 1) - 1, 'day').toISOString(),
  to: dayjs().endOf('day').toISOString(),
});

export const getUsageSummary = (days, params) => client({
  method: 'GET',
  url: '/usage/summary',
  params: { ...usageRange(days), ...(params || {}) },
}).then(({ data }) => data);

export const getUsageUserEvents = (uid, days) => client({
  method: 'GET',
  url: `/usage/users/${uid}/events`,
  params: usageRange(days),
}).then(({ data }) => data);

export const getUsageBreakdown = (days, params) => client({
  method: 'GET',
  url: '/usage/breakdown',
  params: { ...usageRange(days), ...params },
}).then(({ data }) => data);

export const syncMondayUsers = () => client({
  method: 'POST',
  url: '/monday/users/sync',
}).then(({ data }) => data);

// ========== ERROR REPORTING ==========

const truncateErrorField = (value, maxLength = 2000) => {
  if (value === null || value === undefined) return null;
  const serialised = typeof value === 'string' ? value : JSON.stringify(value);
  if (!serialised) return null;
  return serialised.length > maxLength
    ? `${serialised.slice(0, maxLength)}...[truncated]`
    : serialised;
};

const buildClientErrorPayload = (error, context = {}) => {
  const endpoint = error?.config?.url || null;
  const method = error?.config?.method ? error.config.method.toUpperCase() : null;
  const status = error?.response?.status || null;

  return {
    source: 'matrix-web-client',
    timestamp: new Date().toISOString(),
    error: {
      name: error?.name || 'Error',
      message: error?.message || 'Unknown client error',
      stack: error?.stack || null,
      endpoint,
      method,
      status,
      code: error?.code || null,
      responseData: truncateErrorField(error?.response?.data),
    },
    context: {
      pageUrl: window.location?.href || null,
      pagePath: window.location?.pathname || null,
      userAgent: window.navigator?.userAgent || null,
      ...context,
    },
  };
};

export function reportError(error, context = {}) {
  return client({
    method: 'POST',
    url: '/report-client-error',
    data: buildClientErrorPayload(error, context),
  }).catch(() => {
    console.log('Error reporting error');
  });
}

// ========== SHOPIFY ==========

export const createNewShopifyProduct = (store, data, extras = {}) => client({
  method: 'POST',
  url: '/shopify/products/create',
  params: {
    store,
  },
  data: extras.productMatrixId != null
    ? { ...data, productMatrixId: extras.productMatrixId }
    : data,
}).then(({ data: res }) => {
  const linked = res?.links?.linked;
  const failed = res?.links?.failed;
  if (failed > 0) {
    showNotification(
      'warning',
      `Product created. Linked ${linked || 0} SKU(s); ${failed} could not be linked.`,
    );
  } else if (linked > 0) {
    showNotification('success', `Product created and linked to ${linked} SKU(s).`);
  } else {
    showNotification('success', 'Product created!');
  }
  return res;
})
  .catch((error) => {
    // TODO: Switch back to notification error eventually
    // showNotification('error', 'Error submitting request');
    Modal.error({
      title: 'Error creating product',
      centered: true,
      content: (
        'Please check your Shopify store in case of partially created product.'
      ),
    });
    // Allow error to be caught where method was called
    return Promise.reject(error);
  });

export const getAvailableShopifyProducts = (store, { refresh = false } = {}) => client({
  method: 'GET',
  url: '/shopify/products/available',
  params: {
    store,
    ...(refresh ? { refresh: 1 } : {}),
  },
}).then(({ data }) => data) // { products, fetchedAt }
  .catch(() => {
    showNotification('error', 'Error loading products');
  });

export const createShopifyProductRequest = (store, data, idempotencyKey) => client({
  method: 'POST',
  url: '/shopify/products/requests',
  params: {
    store,
  },
  data: {
    ...data,
    idempotencyKey,
  },
}).then(() => {})
  .catch((error) => {
    const productCount = Array.isArray(data?.products) ? data.products.length : 0;
    const variantCount = Array.isArray(data?.products)
      ? data.products.reduce(
        (count, product) => count + (Array.isArray(product?.variants) ? product.variants.length : 0),
        0,
      )
      : 0;
    reportError(error, {
      feature: 'webstore-order-submit',
      store,
      requestType: data?.type || null,
      ship: Boolean(data?.ship),
      shippingRateCode: data?.shippingRateCode || null,
      hasBilling: Boolean(data?.billing),
      xeroContactId: data?.billing?.xeroContactId || null,
      productCount,
      variantCount,
    });
    // TODO: Switch back to notification error eventually
    // showNotification('error', 'Error submitting request');
    Modal.error({
      title: 'Error submitting request',
      centered: true,
      content: (
        `However, your request may still be partially submitted.
        So before trying again please contact jamie@garageproject.co.nz so
        that this can be checked to avoid duplicate requests.`
      ),
    });
    // Allow error to be caught where method was called
    return Promise.reject(error);
  });

export const getShopifyProductRequests = (store, status) => client({
  method: 'GET',
  url: '/shopify/products/requests',
  params: {
    store,
    status,
  },
}).then(({ data }) => data.map(({
  // Convert dates to Day.js objects
  dateRequested,
  ...request
}) => ({
  ...request,
  dateRequested: dayjs(dateRequested),
}))).catch(() => {
  showNotification('error', 'Error loading requests');
});

export const updateShopifyProductSetting = (store, productId, settingKey, value) => client({
  method: 'PUT',
  url: `/shopify/products/${productId}/settings/${settingKey}`,
  params: {
    store,
  },
  data: { value },
}).catch((error) => {
  showNotification('error', 'Error updating product setting');
  // Allow error to be caught where method was called
  return Promise.reject(error);
});

export const approveShopifyProductRequest = (requestId) => client({
  method: 'PUT',
  url: `/shopify/products/requests/${requestId}/status`,
  data: {
    status: 'approved',
  },
}).then(() => {
  showNotification('success', 'Request approved!');
}).catch((error) => {
  showNotification('error', 'Error approving request');
  // Allow error to be caught where method was called
  return Promise.reject(error);
});

export const rejectShopifyProductRequest = (requestId) => client({
  method: 'PUT',
  url: `/shopify/products/requests/${requestId}/status`,
  data: {
    status: 'rejected',
  },
}).then(() => {
  showNotification('success', 'Request rejected!');
}).catch((error) => {
  showNotification('error', 'Error rejecting request');
  // Allow error to be caught where method was called
  return Promise.reject(error);
});

// ========== SHIPPING ==========

// Gets all shipping rates
export const getShippingRates = (shipFromCountry) => client({
  method: 'GET',
  url: '/shopify/shipping-rates',
  params: {
    shipFromCountry,
  },
}).then(({ data }) => data)
  .catch(() => {
    showNotification('error', 'Error loading shipping rates');
  });

// Evalutes available shipping rates for an address
export const evaluateShippingRates = ({
  store, destination, variants, internalWebstore,
}) => client({
  method: 'POST',
  url: '/shopify/shipping-rates',
  params: {
    store,
    internalWebstore,
  },
  data: {
    rate: {
      destination,
      items: variants,
    },
  },
}).then(({ data }) => data)
  .catch((error) => {
    reportError(error, {
      feature: 'webstore-shipping-rates',
      store,
      internalWebstore: Boolean(internalWebstore),
      destinationCountry: destination?.country || null,
      destinationProvince: destination?.province || null,
      destinationPostalCode: destination?.postal_code || null,
      variantCount: Array.isArray(variants) ? variants.length : 0,
    });
    showNotification('error', 'Error loading shipping rates');
    return Promise.reject(error);
  });

// ========== XERO ==========

export const getCustomerContacts = (countryCode, searchTerm = null, cancelToken = null) => client({
  method: 'GET',
  url: '/xero/customer-contacts',
  params: {
    countryCode,
    searchTerm,
  },
  cancelToken, // TODO: Ignore cancelled request error when debouncing
}).then(({ data }) => data);

/** Fetch all product SKUs (for pricing proposal SKU picker). */
export const getAllProductSkus = () => client({
  method: 'GET',
  url: '/product-skus',
}).then(({ data }) => data);

// ========== KOUNTA ==========

export const importKountaCsv = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return client({
    method: 'POST',
    url: '/kounta/import',
    data: formData,
  }).then(({ data }) => data);
};

export const getKountaProducts = (params = {}) => client({
  method: 'GET',
  url: '/kounta/products',
  params,
}).then(({ data }) => data);

export const getKountaCandidates = (skuKey) => client({
  method: 'GET',
  url: `/kounta/products/candidates/${skuKey}`,
}).then(({ data }) => data);

export const linkKountaProduct = (id, productSkuKey, servingType) => client({
  method: 'PUT',
  url: `/kounta/products/${id}/link`,
  data: {
    productSkuKey,
    ...(servingType !== undefined && servingType !== null ? { servingType } : {}),
  },
}).then(({ data }) => data);

export const unlinkKountaProduct = (id) => client({
  method: 'PUT',
  url: `/kounta/products/${id}/unlink`,
}).then(({ data }) => data);

// ========== PRICING ==========

export const listPricingProposals = () => client({
  method: 'GET',
  url: '/pricing/proposals',
}).then(({ data }) => data);

export const createPricingProposal = (name, notes) => client({
  method: 'POST',
  url: '/pricing/proposals',
  data: { name, notes },
}).then(({ data }) => data);

export const getPricingProposal = (id) => client({
  method: 'GET',
  url: `/pricing/proposals/${id}`,
}).then(({ data }) => data);

export const updatePricingProposal = (id, updates) => client({
  method: 'PUT',
  url: `/pricing/proposals/${id}`,
  data: updates,
}).then(({ data }) => data);

export const deletePricingProposal = (id) => client({
  method: 'DELETE',
  url: `/pricing/proposals/${id}`,
}).then(({ data }) => data);

export const addProposalItems = (proposalId, skuKeys) => client({
  method: 'POST',
  url: `/pricing/proposals/${proposalId}/items`,
  data: { skuKeys },
}).then(({ data }) => data);

export const updateProposalItem = (proposalId, itemId, proposedPrices) => client({
  method: 'PUT',
  url: `/pricing/proposals/${proposalId}/items/${itemId}`,
  data: { proposedPrices },
}).then(({ data }) => data);

export const deleteProposalItem = (proposalId, itemId) => client({
  method: 'DELETE',
  url: `/pricing/proposals/${proposalId}/items/${itemId}`,
}).then(({ data }) => data);

export const applyPricingProposal = (id) => client({
  method: 'POST',
  url: `/pricing/proposals/${id}/apply`,
}).then(({ data }) => data);

/** Draft edits keyed by proposal item id — merged server-side with saved proposed_prices */
export const previewPricingApply = (proposalId, proposedOverrides) => client({
  method: 'POST',
  url: `/pricing/proposals/${proposalId}/preview-apply`,
  data: { proposedOverrides },
}).then(({ data }) => data);

export const previewPricingCin7 = (proposalId, proposedOverrides) => client({
  method: 'POST',
  url: `/pricing/proposals/${proposalId}/preview-cin7`,
  data: { proposedOverrides },
}).then(({ data }) => data);

export const previewRecoverDb = (proposalId) => client({
  method: 'GET',
  url: `/pricing/proposals/${proposalId}/preview-recover-db`,
}).then(({ data }) => data);

export const previewRecoverCin7 = (proposalId) => client({
  method: 'GET',
  url: `/pricing/proposals/${proposalId}/preview-recover-cin7`,
}).then(({ data }) => data);

export const recoverPricingDb = (proposalId, { skuKeys } = {}) => client({
  method: 'POST',
  url: `/pricing/proposals/${proposalId}/recover-db`,
  data: skuKeys?.length ? { skuKeys } : undefined,
}).then(({ data }) => data);

export const recoverPricingCin7 = (proposalId, { skuKeys } = {}) => client({
  method: 'POST',
  url: `/pricing/proposals/${proposalId}/recover-cin7`,
  data: skuKeys?.length ? { skuKeys } : undefined,
  validateStatus: (status) => status === 202 || status === 409 || (status >= 200 && status < 300),
}).then((res) => ({ data: res.data, status: res.status }));

export const pushCin7Prices = (proposalId) => client({
  method: 'POST',
  url: `/pricing/proposals/${proposalId}/push-cin7`,
  validateStatus: (status) => status === 202 || status === 409 || (status >= 200 && status < 300),
}).then((res) => ({ data: res.data, status: res.status }));

export const getCin7PushStatus = () => client({
  method: 'GET',
  url: '/pricing/cin7-push-status',
}).then(({ data }) => data);

export const exportKountaCsv = (proposalId, options = {}) => client({
  method: 'GET',
  url: `/pricing/proposals/${proposalId}/export-kounta`,
  params: options.previous ? { previous: '1' } : undefined,
  responseType: 'blob',
}).then((res) => {
  const url = window.URL.createObjectURL(res.data);
  const link = document.createElement('a');
  link.href = url;
  const filename = options.previous
    ? `kounta_recovery_previous_${proposalId}.csv`
    : `kounta_pricing_${proposalId}.csv`;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
});

export const listMarginTargets = () => client({
  method: 'GET',
  url: '/pricing/margin-targets',
}).then(({ data }) => data);

export const upsertMarginTarget = (payload) => client({
  method: 'PUT',
  url: '/pricing/margin-targets',
  data: payload,
}).then(({ data }) => data);

export const deleteMarginTarget = (id) => client({
  method: 'DELETE',
  url: `/pricing/margin-targets/${id}`,
}).then(({ data }) => data);

export const listPricingRanges = () => client({
  method: 'GET',
  url: '/pricing/ranges',
}).then(({ data }) => data);

export const listPricingRules = () => client({
  method: 'GET',
  url: '/pricing/rules',
}).then(({ data }) => data);

export const upsertPricingRules = (rules) => client({
  method: 'PUT',
  url: '/pricing/rules',
  data: { rules },
}).then(({ data }) => data);

export const checkMondayFormulas = () => client({
  method: 'GET',
  url: '/pricing/monday-formulas',
}).then(({ data }) => data);

export const getPricingDiscrepancyOverview = (params) => client({
  method: 'GET',
  url: '/pricing/discrepancies/overview',
  params,
}).then(({ data }) => data);

/** @param {string} beerKey product_matrix_id or '__unassigned__' */
export const getPricingDiscrepancyBeer = (beerKey, params) => client({
  method: 'GET',
  url: `/pricing/discrepancies/beer/${encodeURIComponent(beerKey)}`,
  params,
}).then(({ data }) => data);

/** @param {{ from?: string, to?: string, platform?: string, status?: string, skuKey?: number, before?: number, limit?: number }} params */
export const getPriceChangeLedger = (params) => client({
  method: 'GET',
  url: '/pricing/price-changes',
  params,
}).then(({ data }) => data);

export const downloadPriceChangeLedgerCsv = (params) => client({
  method: 'GET',
  url: '/pricing/price-changes.csv',
  params,
  responseType: 'blob',
}).then((res) => {
  const url = window.URL.createObjectURL(res.data);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'price_change_ledger.csv');
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
});

export const refreshMondayPriceCache = () => client({
  method: 'POST',
  url: '/pricing/discrepancies/refresh-monday-cache',
}).then(({ data }) => data);

export const resolvePricingDiscrepancies = (payload) => client({
  method: 'POST',
  url: '/pricing/discrepancies/resolve',
  data: payload,
}).then(({ data }) => data);

export const getKountaQueue = () => client({
  method: 'GET',
  url: '/pricing/discrepancies/kounta-queue',
}).then(({ data }) => data);

export const applyKountaQueue = (upToChangeId) => client({
  method: 'POST',
  url: '/pricing/discrepancies/kounta-queue/apply',
  data: { upToChangeId },
}).then(({ data }) => data);

export const downloadAccumulatedKountaCsv = (upTo) => client({
  method: 'GET',
  url: '/pricing/discrepancies/accumulated-kounta-csv',
  params: upTo == null ? undefined : { upTo },
  responseType: 'blob',
}).then((res) => {
  const url = window.URL.createObjectURL(res.data);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'kounta_pricing_discrepancies_accumulated.csv');
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
});

/** Crawl one Shopify store's products into shopify_price_cache and re-run the auto name match. */
export const refreshShopifyPriceCache = (store = 'GPNZ') => client({
  method: 'POST',
  url: '/pricing/discrepancies/refresh-shopify-cache',
  data: { store },
}).then(({ data }) => data);

/** @param {{ store: string, productMatrixId: number }} params */
export const getShopifyLinkCandidates = (params) => client({
  method: 'GET',
  url: '/pricing/discrepancies/shopify-candidates',
  params,
}).then(({ data }) => data);

/** @param {{ skuKey: number, store: string, shopifyProductId, shopifyVariantId? }} payload */
export const setShopifyLink = (payload) => client({
  method: 'PUT',
  url: '/pricing/discrepancies/shopify-link',
  data: payload,
}).then(({ data }) => data);

/** @param {{ store?: string, status?: string }} params */
export const getShopifySkuLinkReview = (params) => client({
  method: 'GET',
  url: '/shopify/sku-links/review',
  params,
}).then(({ data }) => data);

/** @param {{ store: string, productMatrixId: number|string }} params */
export const getShopifySkuLinkCandidates = (params) => client({
  method: 'GET',
  url: '/shopify/sku-links/candidates',
  params,
}).then(({ data }) => data);

/** @param {{ store: string, q: string }} params */
export const searchShopifySkuLinkProducts = (params) => client({
  method: 'GET',
  url: '/shopify/sku-links/search',
  params,
}).then(({ data }) => data);

/** @param {{ productMatrixId, store: string, shopifyProductId }} payload */
export const setShopifyBeerLink = (payload) => client({
  method: 'PUT',
  url: '/shopify/sku-links/beer',
  data: payload,
}).then(({ data }) => data);

/** @param {{ skuKey, store: string, shopifyProductId, shopifyVariantId }} payload */
export const setShopifySkuLink = (payload) => client({
  method: 'PUT',
  url: '/shopify/sku-links',
  data: payload,
}).then(({ data }) => data);

/** @param {{ status?: string, needs?: string }} params */
export const getPlatformLinkReview = (params) => client({
  method: 'GET',
  url: '/platform-links/review',
  params,
}).then(({ data }) => data);

/** @param {string|number} productMatrixId */
export const getPlatformLinkBeer = (productMatrixId) => client({
  method: 'GET',
  url: `/platform-links/review/${encodeURIComponent(productMatrixId)}`,
}).then(({ data }) => data);

/** @param {{ q: string, containerType?: string }} params */
export const searchPlatformCin7 = (params) => client({
  method: 'GET',
  url: '/platform-links/cin7/search',
  params,
}).then(({ data }) => data);

/** @param {{ skuKey, cin7Sku, dearCode }} payload */
export const setPlatformCin7Link = (payload) => client({
  method: 'PUT',
  url: '/platform-links/cin7',
  data: payload,
}).then(({ data }) => data);

/** @param {{ skuKey: number }} payload */
export const acceptPlatformKounta = (payload) => client({
  method: 'PUT',
  url: '/platform-links/kounta/accept',
  data: payload,
}).then(({ data }) => data);

/** @param {{ skuKey: number }} payload */
export const unlinkPlatformKounta = (payload) => client({
  method: 'PUT',
  url: '/platform-links/kounta/unlink',
  data: payload,
}).then(({ data }) => data);

/** @param {{ store: string, shopifyVariantId, price: number }} payload */
export const pushShopifyVariantPrice = (payload) => client({
  method: 'POST',
  url: '/pricing/discrepancies/shopify-push',
  data: payload,
}).then(({ data }) => data);

/** @param {{ items: Array<{ skuKey: number, resolutions: Array<{ field, value }> }> }} payload */
export const resolvePricingDiscrepanciesBulk = (payload) => client({
  method: 'POST',
  url: '/pricing/discrepancies/resolve-bulk',
  data: payload,
}).then(({ data }) => data);

// ========== TRADE SEGMENTATION ==========

/**
 * Trigger one scoring run per region.
 * @param {string[]} regions - e.g. ['NZ', 'AU']
 * @param {boolean} autoApply - when true the run skips the HITL gate and applies immediately.
 * NOTE: the request interceptor decamelizes the body, so `autoApply` is sent as `auto_apply`.
 * @returns {Promise<{ runs: Array<{ id, region, status }> }>}
 */
export const createSegmentationRuns = (regions, autoApply) => client({
  method: 'POST',
  url: '/segmentation/runs',
  data: { regions, autoApply },
}).then(({ data }) => data)
  .catch((error) => {
    showNotification('error', 'Error starting segmentation run');
    return Promise.reject(error);
  });

/** Recent runs. Normalised to an array whether the API returns `[...]` or `{ runs: [...] }`. */
export const getSegmentationRuns = (limit) => client({
  method: 'GET',
  url: '/segmentation/runs',
  params: limit ? { limit } : {},
}).then(({ data }) => {
  if (Array.isArray(data)) return data;
  return Array.isArray(data?.runs) ? data.runs : [];
}).catch((error) => {
  showNotification('error', 'Error loading segmentation runs');
  return Promise.reject(error);
});

/** Run detail including summaryJson + flat cin7/monday ok/fail/skip totals. */
export const getSegmentationRun = (runId) => client({
  method: 'GET',
  url: `/segmentation/runs/${runId}`,
}).then(({ data }) => data)
  .catch((error) => {
    showNotification('error', 'Error loading segmentation run');
    return Promise.reject(error);
  });

/**
 * Paginated + filterable audit rows for a run.
 * Backend returns `{ rows, changes, total, limit, offset }` (`changes` = legacy alias).
 * @param {number|string} runId
 * @param {{ system?: 'cin7'|'monday', status?: string, limit?: number, offset?: number }} params
 * @returns {Promise<{ rows: Array, total: number }>}
 */
export const getSegmentationRunChanges = (runId, params = {}) => client({
  method: 'GET',
  url: `/segmentation/runs/${runId}/changes`,
  params,
}).then(({ data }) => {
  let rows = [];
  if (Array.isArray(data?.rows)) rows = data.rows;
  else if (Array.isArray(data?.changes)) rows = data.changes;
  return { rows, total: data?.total ?? rows.length };
}).catch((error) => {
  showNotification('error', 'Error loading segmentation change log');
  return Promise.reject(error);
});

/** Approve an awaiting-approval run — starts the apply. */
export const approveSegmentationRun = (runId) => client({
  method: 'POST',
  url: `/segmentation/runs/${runId}/approve`,
}).then(({ data }) => {
  showNotification('success', 'Run approved — applying to Cin7 and Monday');
  return data;
}).catch((error) => {
  showNotification('error', 'Error approving segmentation run');
  return Promise.reject(error);
});

/** Re-apply a failed run, or a completed run that still has failures, under the same run id. */
export const retrySegmentationRun = (runId) => client({
  method: 'POST',
  url: `/segmentation/runs/${runId}/retry`,
}).then(({ data }) => {
  showNotification('success', 'Retrying apply for this run');
  return data;
}).catch((error) => {
  const detail = error?.response?.data?.error;
  const description = typeof detail === 'string' ? detail : undefined;
  showNotification('error', 'Error retrying segmentation run', description);
  return Promise.reject(error);
});

/** Reject an awaiting-approval run. */
export const rejectSegmentationRun = (runId) => client({
  method: 'POST',
  url: `/segmentation/runs/${runId}/reject`,
}).then(({ data }) => {
  showNotification('success', 'Run rejected');
  return data;
}).catch((error) => {
  showNotification('error', 'Error rejecting segmentation run');
  return Promise.reject(error);
});

// ========== PRODUCTION FORECASTING HUB ==========

const forecastingError = (title) => (error) => {
  showNotification('error', title, error?.response?.data?.error);
  return Promise.reject(error);
};

/**
 * Full brew plan: forecast, stock projection and brew recommendations per Core beer.
 * @param {{ refresh?: boolean }} options refresh bypasses the backend's 15 minute BigQuery cache
 */
export const getForecastingPlan = ({ refresh = false } = {}) => client({
  method: 'GET',
  url: '/forecasting/plan',
  params: refresh ? { refresh: 1 } : {},
}).then(({ data }) => data)
  .catch(forecastingError('Error loading the forecast'));

/** Start the BigQuery forecast script. Returns once the job is queued, not when it finishes. */
export const startForecastingRun = () => client({
  method: 'POST',
  url: '/forecasting/run',
}).then(({ data }) => data)
  .catch(forecastingError('Error starting the forecast'));

/** Polled while a run is open. No toast: failures here would fire every few seconds. */
export const getForecastingRunStatus = () => client({
  method: 'GET',
  url: '/forecasting/run',
}).then(({ data }) => data);

export const getForecastingAccuracy = () => client({
  method: 'GET',
  url: '/forecasting/accuracy',
}).then(({ data }) => data)
  .catch(forecastingError('Error loading forecast accuracy'));

/** Save the live mix and rebuild brew-plan litres. Does not retrain. */
export const setForecastingBlend = (weightPercent) => client({
  method: 'POST',
  url: '/forecasting/blend',
  data: { weightPercent },
}).then(({ data }) => {
  showNotification('success', 'Brew plan mix updated');
  return data;
}).catch(forecastingError('Error updating the forecast mix'));

export const getForecastingSettings = () => client({
  method: 'GET',
  url: '/forecasting/settings',
}).then(({ data }) => data)
  .catch(forecastingError('Error loading forecasting rules'));

/** Partial update; a null value resets that setting to its default. */
export const updateForecastingSettings = (settings) => client({
  method: 'PUT',
  url: '/forecasting/settings',
  data: { settings },
}).then(({ data }) => {
  showNotification('success', 'Rules saved');
  return data;
}).catch(forecastingError('Error saving rules'));

export const saveForecastingBeerRule = (rule) => client({
  method: 'PUT',
  url: '/forecasting/beer-rules',
  data: rule,
}).then(({ data }) => {
  showNotification('success', `Rules saved for ${rule.beerName}`);
  return data;
}).catch(forecastingError('Error saving beer rules'));

export const deleteForecastingBeerRule = (beerName) => client({
  method: 'DELETE',
  url: '/forecasting/beer-rules',
  params: { beer_name: beerName },
}).then(({ data }) => data)
  .catch(forecastingError('Error resetting beer rules'));

/** Replace a mixed pack's components; an empty list turns decomposition off for that pack. */
export const saveForecastingMixComponents = (mixBeerName, components) => client({
  method: 'PUT',
  url: '/forecasting/mix-components',
  data: { mixBeerName, components },
}).then(({ data }) => {
  showNotification('success', `${mixBeerName} composition saved`);
  return data;
}).catch(forecastingError('Error saving mixed pack'));

export const getForecastingBrews = () => client({
  method: 'GET',
  url: '/forecasting/brews',
}).then(({ data }) => data.brews || [])
  .catch(forecastingError('Error loading the production schedule'));

export const createForecastingBrew = (brew) => client({
  method: 'POST',
  url: '/forecasting/brews',
  data: brew,
}).then(({ data }) => {
  showNotification('success', `${brew.beerName} brew added to the schedule`);
  return data.brew;
}).catch(forecastingError('Error adding brew'));

/** Bulk add (CSV paste). Rejects with `response.data.errors` listing invalid rows. */
export const createForecastingBrewsBulk = (brews) => client({
  method: 'POST',
  url: '/forecasting/brews/bulk',
  data: { brews },
}).then(({ data }) => {
  showNotification('success', `${data.brews.length} brews added to the schedule`);
  return data.brews;
}).catch(forecastingError('Error adding brews'));

export const updateForecastingBrew = (id, brew) => client({
  method: 'PUT',
  url: `/forecasting/brews/${id}`,
  data: brew,
}).then(({ data }) => {
  showNotification('success', 'Brew updated');
  return data.brew;
}).catch(forecastingError('Error updating brew'));

export const deleteForecastingBrew = (id) => client({
  method: 'DELETE',
  url: `/forecasting/brews/${id}`,
}).then(({ data }) => {
  showNotification('success', 'Brew removed');
  return data;
}).catch(forecastingError('Error removing brew'));

export const createForecastingEvent = (event) => client({
  method: 'POST',
  url: '/forecasting/events',
  data: event,
}).then(({ data }) => {
  showNotification('success', `${event.name} added`);
  return data.event;
}).catch(forecastingError('Error adding event'));

export const updateForecastingEvent = (id, event) => client({
  method: 'PUT',
  url: `/forecasting/events/${id}`,
  data: event,
}).then(({ data }) => {
  showNotification('success', 'Event updated');
  return data.event;
}).catch(forecastingError('Error updating event'));

export const deleteForecastingEvent = (id) => client({
  method: 'DELETE',
  url: `/forecasting/events/${id}`,
}).then(({ data }) => {
  showNotification('success', 'Event removed');
  return data;
}).catch(forecastingError('Error removing event'));
